import { ReactNode, useEffect, useRef, useState } from 'react';
import {
  Dropdown,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  Avatar,
  Box,
} from '@mui/joy';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { AccountInfo } from '../AccountInfo/AccountInfo';
import { LogoutBtnBase } from '../LogoutBtnBase/LogoutBtnBase';
import { ProfileMenuItemBase } from '../ProfileMenuItemBase/ProfileMenuItemBase';
import Divider from '@mui/joy/Divider';

interface ProfileMenuProps {
  onLogout: () => void;
  onProfileClick?: () => void;
  slotContent?: ReactNode;
  profilePicture?: string;
  selectedRole?: {
    department: string;
    role: string;
    jobRole?: string;
  };
}

export function ProfileMenu({
  onLogout,
  onProfileClick,
  slotContent,
  profilePicture,
  selectedRole,
}: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef}>
      <Dropdown open={open} onOpenChange={(_, newOpen) => setOpen(newOpen)}>
        <MenuButton
          slots={{ root: Avatar }}
          slotProps={{
            root: {
              size: 'sm',
              variant: 'outlined',
              sx: {
                cursor: 'pointer',
                bgcolor: 'background.level1',
                color: 'neutral.600',
              },
            },
          }}
        >
          {profilePicture ? (
            <Avatar src={profilePicture} />
          ) : (
            <AccountCircleIcon fontSize="large" sx={{ fontSize: '40px' }} />
          )}
        </MenuButton>

        <Menu
          placement="bottom-end"
          sx={{
            p: 0,
            minWidth: 220,
            borderRadius: 'md',
            boxShadow: 'md',
            zIndex: 9999,
          }}
        >
          <MenuList variant="plain" sx={{ p: 0 }}>
            <Box
              sx={{
                p: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'default', // optional
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <AccountInfo 
                showDetailedRole={!!selectedRole} 
                selectedRole={selectedRole}
                profilePicture={profilePicture}
              />
            </Box>
            <Divider />
            {slotContent}
            {slotContent && <Divider />}
            {onProfileClick && (
              <>
                <MenuItem
                  sx={{ p: 0, px: 1 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <ProfileMenuItemBase 
                    onClick={() => {
                      onProfileClick();
                      setOpen(false);
                    }} 
                  />
                </MenuItem>
                <Divider />
              </>
            )}
            <MenuItem
              sx={{ p: 0, px: 1 }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <LogoutBtnBase onClick={onLogout} />
            </MenuItem>
          </MenuList>
        </Menu>
      </Dropdown>
    </div>
  );
}
